import html2canvas from "html2canvas";
import { isIOSDevice } from "./captureEnvironment";

// Silently snapshots the CURRENT window (visible viewport) with no browser
// "choose what to share" prompt, by rasterising the live DOM. Used as the
// default for "Report a Bug"; the screen-share picker (captureScreenWhenReady)
// remains available on desktop for when a different window is wanted.
//
// Task 24511 - this is now the ONLY capture route on an iPhone/iPad, because
// iOS Safari has no getDisplayMedia at all. Everything below that looks like
// belt-and-braces is there because iOS is the strict one:
//   * Safari refuses to allocate a canvas over ~16.7M pixels and hands back a
//     blank one instead of throwing, so the scale is capped.
//   * allowTaint was letting a single non-CORS image (an avatar, a CDN logo)
//     taint the canvas, after which toDataURL throws SecurityError and the
//     whole screenshot is lost. Skipping that one image is the better trade.
//   * the clone used to be laid out at full document height, which re-runs the
//     page's media queries and 100vh rules at a viewport the phone never had -
//     the capture came out looking nothing like the screen, when iOS managed
//     to render the oversized iframe at all.

// Safari's hard canvas ceiling (iOS and macOS alike). Cross it and you get a
// blank canvas back with no error.
const MAX_CANVAS_PIXELS = 16777216;
const MAX_CANVAS_EDGE = 4096;

// A capture that has not finished by now never will - do not leave the bug
// widget hanging on a phone with a slow connection.
const CAPTURE_TIMEOUT_MS = 15000;

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Screenshot capture timed out.")), ms)
    ),
  ]);

// What the user can actually see. On iOS the visual viewport moves independently
// of the layout viewport (collapsing URL bar, pinch zoom), so window.scrollY
// alone can be pointing somewhere the user is not looking.
const getVisibleRegion = () => {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;

  if (vv) {
    return {
      x: Math.max(0, Math.round(vv.pageLeft)),
      y: Math.max(0, Math.round(vv.pageTop)),
      width: Math.max(1, Math.round(vv.width)),
      height: Math.max(1, Math.round(vv.height)),
    };
  }

  return {
    x: Math.max(0, Math.round(window.scrollX || 0)),
    y: Math.max(0, Math.round(window.scrollY || 0)),
    width: Math.max(1, window.innerWidth),
    height: Math.max(1, window.innerHeight),
  };
};

// Highest scale that keeps the canvas inside Safari's limits.
const getSafeScale = (width, height) => {
  const requested = window.devicePixelRatio || 1;

  // A 3x iPhone screen is a ~9MB PNG before it even reaches Konva's export.
  // 2x is still retina-sharp and roughly half the payload.
  const ceiling = isIOSDevice() ? 2 : 3;

  const byEdge = MAX_CANVAS_EDGE / Math.max(width, height);
  const byArea = Math.sqrt(MAX_CANVAS_PIXELS / (width * height));

  return Math.max(0.5, Math.min(requested, ceiling, byEdge, byArea));
};

// A capture that came back fully transparent or a single flat colour is a
// failure wearing a success costume - better to report nothing than to hand the
// user an empty grey box and let them think they attached something.
const isBlankCanvas = (canvas) => {
  try {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx || !canvas.width || !canvas.height) return true;

    const w = Math.min(canvas.width, 400);
    const h = Math.min(canvas.height, 400);
    const { data } = ctx.getImageData(0, 0, w, h);

    let opaque = 0;
    let sum = 0;
    let sumSquares = 0;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] > 8) opaque += 1;
      const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
      sum += brightness;
      sumSquares += brightness * brightness;
    }

    const count = data.length / 4;
    if (!count) return true;
    if (opaque / count < 0.02) return true; // effectively transparent

    const mean = sum / count;
    const stdDev = Math.sqrt(Math.max(sumSquares / count - mean * mean, 0));

    return stdDev < 1.5;
  } catch (err) {
    // getImageData throws on a tainted canvas; that is not proof it is blank.
    return false;
  }
};

// The page's real background, so a page that paints its colour on <html> does
// not come out as a transparent PNG.
const getPageBackgroundColor = () => {
  try {
    const bodyBg = getComputedStyle(document.body).backgroundColor;
    if (bodyBg && !/^(transparent|rgba\(0, 0, 0, 0\))$/.test(bodyBg)) {
      return bodyBg;
    }

    const htmlBg = getComputedStyle(document.documentElement).backgroundColor;
    if (htmlBg && !/^(transparent|rgba\(0, 0, 0, 0\))$/.test(htmlBg)) {
      return htmlBg;
    }
  } catch (err) {
    /* fall through */
  }

  return "#ffffff";
};

/**
 * @param {object}   [options]
 * @param {string[]} [options.ignoreSelectors] extra CSS selectors to leave out
 *        of the shot (used to hide the bug-report modal itself on a re-capture).
 * @returns {Promise<string>} a PNG data URL, or "" if the capture failed.
 */
export const captureCurrentWindow = async ({ ignoreSelectors = [] } = {}) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return "";
  }

  const region = getVisibleRegion();
  const scale = getSafeScale(region.width, region.height);

  const shouldIgnore = (el) => {
    if (!el || typeof el.hasAttribute !== "function") return false;

    // Never bake our own bug widget / picker overlay into the shot.
    if (el.hasAttribute("data-bug-picker")) return true;
    if (el.classList?.contains?.("generate-feedback-button-frame")) return true;

    return ignoreSelectors.some((selector) => {
      try {
        return el.matches?.(selector);
      } catch (err) {
        return false;
      }
    });
  };

  try {
    const canvas = await withTimeout(
      html2canvas(document.body, {
        useCORS: true,
        // Deliberately false - see the note at the top of the file.
        allowTaint: false,
        logging: false,
        backgroundColor: getPageBackgroundColor(),
        scale,
        imageTimeout: 8000,
        // Crop to what the user can currently see, not the whole scroll height.
        x: region.x,
        y: region.y,
        width: region.width,
        height: region.height,
        // Lay the clone out at the REAL viewport so media queries, 100vh and
        // sticky headers resolve the way they do on the device.
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        scrollX: region.x,
        scrollY: region.y,
        ignoreElements: shouldIgnore,
      }),
      CAPTURE_TIMEOUT_MS
    );

    if (!canvas || !canvas.width || !canvas.height) return "";
    if (isBlankCanvas(canvas)) {
      console.warn("captureCurrentWindow: capture came back blank.");
      return "";
    }

    return canvas.toDataURL("image/png");
  } catch (err) {
    console.error("captureCurrentWindow failed:", err);
    return "";
  }
};

export default captureCurrentWindow;
